import { z } from 'zod';
import type {
    CardPointeCredentials,
    CardPointeCredentialsPublic,
    CardPointeCredentialsSecret,
    CardPointeGatewayCredentials,
    CardPointeTerminalCredentials,
    DraftCardPointeCredentials,
} from '../../../types/ipg/cardpointe/cardpointe-credential.types';

export const CardPointeCredentialsPublicSchema: z.ZodType<CardPointeCredentialsPublic> = z
    .object({
        username: z.string().min(1).optional(),
    })
    .strict();

export const StoredCardPointeCredentialsPublicParser: z.ZodType<CardPointeCredentialsPublic> = z
    .object({
        username: z.string().min(1).optional(),
        merchantId: z.string().min(1).optional(),
    })
    .strict()
    .transform(
        ({ username }): CardPointeCredentialsPublic => (username === undefined ? {} : { username })
    );

export const CardPointeGatewayCredentialsSchema: z.ZodType<CardPointeGatewayCredentials> = z
    .object({
        username: z.string().min(1),
        password: z.string().min(1),
    })
    .strict();

export const CardPointeTerminalCredentialsSchema: z.ZodType<CardPointeTerminalCredentials> = z
    .object({
        terminalApiKey: z.string().min(1),
    })
    .strict();

export const CardPointeCredentialsSecretSchema: z.ZodType<CardPointeCredentialsSecret> = z.union([
    CardPointeTerminalCredentialsSchema,
    CardPointeGatewayCredentialsSchema,
]);

export const CardPointeCredentialsSchema: z.ZodType<CardPointeCredentials> =
    CardPointeCredentialsSecretSchema;

export const StoredCardPointeCredentialsParser: z.ZodType<CardPointeCredentials> = z.union([
    z
        .object({
            terminalApiKey: z.string().min(1),
            merchantId: z.string().optional(),
            username: z.string().optional(),
            password: z.string().optional(),
        })
        .strip()
        .transform(
            ({ terminalApiKey }): CardPointeTerminalCredentials => ({
                terminalApiKey,
            })
        ),
    z
        .object({
            username: z.string().min(1),
            password: z.string().min(1),
            merchantId: z.string().optional(),
        })
        .strip()
        .transform(
            ({ username, password }): CardPointeGatewayCredentials => ({
                username,
                password,
            })
        ),
]);

export const DraftCardPointeCredentialsSchema: z.ZodType<DraftCardPointeCredentials> = z.union([
    z
        .object({
            username: z.string().min(1).optional(),
            password: z.string().min(1).optional(),
        })
        .strict()
        .refine((value) => Object.keys(value).length > 0, {
            message: 'At least one credential field is required',
        }),
    z
        .object({
            terminalApiKey: z.string().min(1).optional(),
        })
        .strict()
        .refine((value) => Object.keys(value).length > 0, {
            message: 'At least one credential field is required',
        }),
]);
